import React, { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import type { MetadataFieldSchema } from '../../api/JsdhNotifikaceService/generated/models/TypKontroly';
import type {
    KontrolaStatusResponse,
    UzivatelKontrola,
    CreateKontrolaRequest,
} from '../../api/JsdhNotifikaceService/generated/models/Kontrola';
import {
    useKontrolyHistorieQuery,
    useCreateKontrolaMutation,
    useUpdateKontrolaMutation,
    useDeleteKontrolaMutation,
} from '../../api/JsdhNotifikaceService/service/KontrolyMutations';
import { useActiveTechnikaQuery } from '../../api/JsdhNotifikaceService/service/TechnikaMutations';

// ==========================================
// HELPER: Metadata Form Fields
// ==========================================

const MetadataFields: React.FC<{
    schema: MetadataFieldSchema[];
    values: Record<string, any>;
    onChange: (values: Record<string, any>) => void;
}> = ({ schema, values, onChange }) => {
    const { data: technika } = useActiveTechnikaQuery();

    return (
        <>
            {schema.map((field) => {
                if (field.type === 'technika') {
                    return (
                        <FormControl key={field.key} fullWidth margin="dense" size="small">
                            <InputLabel>{field.label}{field.required ? ' *' : ''}</InputLabel>
                            <Select
                                value={values[field.key] ?? ''}
                                label={`${field.label}${field.required ? ' *' : ''}`}
                                onChange={(e) => onChange({ ...values, [field.key]: e.target.value })}
                            >
                                <MenuItem value="">— Nevybráno —</MenuItem>
                                {technika?.map((t) => (
                                    <MenuItem key={t.id} value={t.id}>
                                        {t.volaciZnak} - {t.nazev}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                }

                if (field.type === 'select' && field.options) {
                    return (
                        <FormControl key={field.key} fullWidth margin="dense" size="small">
                            <InputLabel>{field.label}{field.required ? ' *' : ''}</InputLabel>
                            <Select
                                value={values[field.key] ?? ''}
                                label={`${field.label}${field.required ? ' *' : ''}`}
                                onChange={(e) => onChange({ ...values, [field.key]: e.target.value })}
                            >
                                <MenuItem value="">— Nevybráno —</MenuItem>
                                {field.options.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                }

                return (
                    <TextField
                        key={field.key}
                        label={field.label}
                        type={field.type === 'number' ? 'number' : 'text'}
                        value={values[field.key] ?? ''}
                        onChange={(e) => onChange({
                            ...values,
                            [field.key]: field.type === 'number'
                                ? (e.target.value === '' ? '' : Number(e.target.value))
                                : e.target.value
                        })}
                        required={field.required}
                        fullWidth
                        margin="dense"
                        size="small"
                    />
                );
            })}
        </>
    );
};

// ==========================================
// DIALOG: Zaznamenat kontrolu
// ==========================================

const KontrolaDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    uzivatelId: number;
    uzivatelJmeno: string;
    kontrolaStatus: KontrolaStatusResponse;
}> = ({ open, onClose, uzivatelId, uzivatelJmeno, kontrolaStatus }) => {
    const [datumSplneni, setDatumSplneni] = useState<Dayjs | null>(dayjs());
    const [poznamka, setPoznamka] = useState('');
    const [metadataValues, setMetadataValues] = useState<Record<string, any>>({});
    const [editingId, setEditingId] = useState<number | null>(null);

    const createMutation = useCreateKontrolaMutation();
    const updateMutation = useUpdateKontrolaMutation();
    const deleteMutation = useDeleteKontrolaMutation();
    const { data: historie } = useKontrolyHistorieQuery(
        open ? uzivatelId : null,
        open ? kontrolaStatus.typKontrolyId : null
    );

    const metadataSchema: MetadataFieldSchema[] = useMemo(() => {
        if (!kontrolaStatus.metadataSchema) return [];
        try {
            return JSON.parse(kontrolaStatus.metadataSchema);
        } catch {
            return [];
        }
    }, [kontrolaStatus.metadataSchema]);

    const handleReset = () => {
        setDatumSplneni(dayjs());
        setPoznamka('');
        setMetadataValues({});
        setEditingId(null);
    };

    const handleClose = () => {
        handleReset();
        onClose();
    };

    const handleEdit = (record: UzivatelKontrola) => {
        setDatumSplneni(dayjs(record.datumSplneni));
        setPoznamka(record.poznamka || '');
        setEditingId(record.id);
        if (record.metadata) {
            try {
                setMetadataValues(JSON.parse(record.metadata));
            } catch {
                setMetadataValues({});
            }
        } else {
            setMetadataValues({});
        }
    };

    const handleSave = () => {
        if (!datumSplneni) return;

        const metadataJson = metadataSchema.length > 0 && Object.keys(metadataValues).some(k => metadataValues[k] !== '' && metadataValues[k] !== undefined)
            ? JSON.stringify(metadataValues)
            : undefined;

        const request: CreateKontrolaRequest = {
            uzivatelId,
            typKontrolyId: kontrolaStatus.typKontrolyId,
            datumSplneni: datumSplneni.format('YYYY-MM-DD'),
            poznamka: poznamka || undefined,
            metadata: metadataJson,
        };

        if (editingId) {
            updateMutation.mutate({ id: editingId, data: request }, {
                onSuccess: () => handleReset(),
            });
        } else {
            createMutation.mutate(request, {
                onSuccess: () => handleReset(),
            });
        }
    };

    const handleDelete = (record: UzivatelKontrola) => {
        if (window.confirm('Opravdu smazat tento záznam?')) {
            deleteMutation.mutate({ id: record.id, uzivatelId });
        }
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth={metadataSchema.length > 2 ? 'md' : 'sm'} fullWidth>
            <DialogTitle>
                {kontrolaStatus.typKontrolyNazev} — {uzivatelJmeno}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <DatePicker
                        label="Datum splnění"
                        value={datumSplneni}
                        onChange={setDatumSplneni}
                        format="DD.MM.YYYY"
                        slotProps={{ textField: { fullWidth: true, size: 'small', margin: 'dense' } }}
                    />

                    <TextField
                        label="Poznámka"
                        value={poznamka}
                        onChange={(e) => setPoznamka(e.target.value)}
                        fullWidth
                        margin="dense"
                        size="small"
                        multiline
                        rows={2}
                    />

                    {metadataSchema.length > 0 && (
                        <MetadataFields
                            schema={metadataSchema}
                            values={metadataValues}
                            onChange={setMetadataValues}
                        />
                    )}
                </Box>

                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!datumSplneni || isSaving}
                        size="small"
                    >
                        {isSaving ? <CircularProgress size={20} /> : editingId ? 'Uložit změny' : 'Zaznamenat'}
                    </Button>
                    {editingId && (
                        <Button variant="outlined" onClick={handleReset} size="small">
                            Zrušit editaci
                        </Button>
                    )}
                </Box>

                {/* Historie */}
                {historie && historie.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>Historie</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Datum splnění</TableCell>
                                        <TableCell>Expirace</TableCell>
                                        {metadataSchema.map((field) => (
                                            <TableCell key={field.key}>{field.label}</TableCell>
                                        ))}
                                        <TableCell>Poznámka</TableCell>
                                        <TableCell align="right">Akce</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {historie.map((record) => {
                                        let meta: Record<string, any> = {};
                                        if (record.metadata) {
                                            try { meta = JSON.parse(record.metadata); } catch { /* ignore */ }
                                        }
                                        return (
                                            <TableRow key={record.id}>
                                                <TableCell>{dayjs(record.datumSplneni).format('DD.MM.YYYY')}</TableCell>
                                                <TableCell>{dayjs(record.datumExpirace).format('DD.MM.YYYY')}</TableCell>
                                                {metadataSchema.map((field) => (
                                                    <TableCell key={field.key}>
                                                        {meta[field.key] != null ? String(meta[field.key]) : '—'}
                                                    </TableCell>
                                                ))}
                                                <TableCell>{record.poznamka || '—'}</TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="Upravit">
                                                        <IconButton size="small" onClick={() => handleEdit(record)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Smazat">
                                                        <IconButton size="small" onClick={() => handleDelete(record)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Zavřít</Button>
            </DialogActions>
        </Dialog>
    );
};

export default KontrolaDialog;
